import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import PropTypes from 'prop-types'
import { useStores } from '../../cls/stores/MobXProvider'
import { DO_LOAD } from '../../config'
import { actLoadUser } from '../../stores/users/actions'
import get from 'lodash/get'
import { getFullItm } from '../../api/utils/api-item'
/**
 * UserString component renders a property from a user object, or if `isForNotification`
 * is true, fetches and displays the name of an arbitrary entity via API.
 *
 * @component
 * @param {Object} props
 * @param {string} props.userId
 *   The ID of the user (or entity) to display.
 * @param {string|Array<string>} props.path
 *   The property path within the user object to display (e.g. 'name' or ['profile','email']).
 *   Ignored when `isForNotification` is true.
 * @param {boolean} [props.include_full=false]
 *   Whether to fetch the full user record when loading.
 * @param {boolean} [props.isForNotification=false]
 *   If true, fetches from `entity_table` instead of the users store and displays the
 *   returned `data.name`.
 * @param {string} [props.entity_table='']
 *   The API table name to query when `isForNotification` is true.
 * @returns {JSX.Element|string} The requested user field or an entity name string.
 */
const UserString = observer(
	({
		userId,
		path,
		include_full = false,
		isForNotification = false,
		entity_table = ''
	}) => {
		if (!userId) {
			return '---'
		}
		// Check if the component is for displaying notifications
		if (isForNotification) {
			// State to store the result of the API call
			const [result, setResult] = useState('')

			// Fetch the item notification to set the name of notification
			useEffect(() => {
				getFullItm(entity_table, {
					id: userId
				})
					.then((response) => {
						setResult(response?.data?.name || 'Entity not found')
					})
					.catch((error) => {
						console.error(error)
						setResult('Entity not found')
					})
			}, [isForNotification, entity_table])

			return result
		} else {
			const { users } = useStores()
			const user = users.requireUserById(userId)

			useEffect(() => {
				// Fetch only if user is empty
				if (user.loaded === DO_LOAD) {
					actLoadUser(users, userId, include_full)
						.catch(console.error)
						.finally(() => {
							users.saveState()
						})
				}
			}, [userId]) // Depend only on userId
			return get(user, [path], '---')
		}
	}
)

UserString.propTypes = {
	userId: PropTypes.string.isRequired,
	path: PropTypes.oneOfType([PropTypes.array, PropTypes.string]).isRequired,
	include_full: PropTypes.bool,
	isForNotification: PropTypes.bool
}

export default UserString
